'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { UserType } from '@diritto/common/account/constants/user.constant';
import { WebViewScreen } from '@diritto/common/app/webview-header.constant';
import {
  ContentSerializeStatus,
  ContentStatus,
  RelayType,
  StudioContentsTabType,
} from '@diritto/common/content/constants';
import { ContestStage } from '@diritto/common/content/types/content-contest-info-types';
import { SectionPosition } from '@diritto/common/home/constants/section-constants';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { Color } from '@diritto/common/style/color';
import { Typography } from '@diritto/common/style/typography';
import { FeatureFlagName } from '@diritto/common/system/constants';
import {
  ClickAction,
  ClickTargetType,
  TrackerExtraInfo,
  TrackerPosition,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import { extractEnumValue } from '@diritto/common/utils/enum';
import { ContestId } from '@diritto/common/content/contest.constants';
import { LocalStorageKey } from '@diritto/common/utils/localStorage';
import { useContestStage } from 'state-hooks/contests/useStageValidation';
import { useGetMyContestContentsQuery } from 'queries/contests/useGetMyContestContentsQuery';
import { MyContestContentList } from 'app-components/studio/contests/MyContestContentList';
import Section from 'app-components/home/sections/Section';
import { MyContentList } from 'app-components/studio/myContents/contents/MyContentList';
import { ListHeader, SectionText } from 'app-components/studio/myContents/ListHeader';
import {
  StudioContentsContestTabItem,
  StudioContentsTabItem,
  Tab,
  TabList,
} from 'app-components/studio/myContents/Tab';
import { ButtonSize } from 'constants/button';
import { useAuthState } from 'contexts/AuthProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { InfoFilledIcon } from 'icons/InfoFilledIcon';
import { useGetSectionsQuery } from 'queries/relay/useGetSectionsQuery';
import { useGetWsContentsQuery } from 'queries/studio/useGetWsContentsQuery';
import { usePostRelayWorkspaceContentsQuery } from 'queries/studio/usePostRelayWorkspaceContentsQuery';
import { DescriptionBlock } from 'ui-components/common/DescriptionBlock';
import { LinkButton } from 'ui-components/common/LinkButton';
import { Loading } from 'ui-components/common/Loading';
import { DirittoStudioLayout } from 'ui-components/layout/DirittoStudioLayout';
import { DirittoStudioLeftMenu } from 'ui-components/navigations/dirittoStudioMenu/DirittoStudioLeftMenu';
import { useFeatureFlagConfig } from 'ui-hooks/useFeatureFlagConfig';
import { useInAppHeader } from 'ui-hooks/useInAppHeader';
import { useInAppStatusBar } from 'ui-hooks/useInAppStatusBar';
import { useRequestCertificate } from 'ui-hooks/useRequestCertificate';
import { showAlertModal } from 'utils/modal';
import { LocalStorage } from 'utils/LocalStorage';
import { SentryService } from 'utils/SentryService';
import { ContentType } from 'app/(root)/studio/contests/[contestId]/contents/add/AddContestContentPage';

const trackerExtraInfo: TrackerExtraInfo = {
  trackerPosition: TrackerPosition.DirittoStudioContentAllList,
};

const contestId = ContestId.Contest2026;

// NOTE: 공모전 기간에만 사용하는 컴포넌트
export function StudioMyContestContentsPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user } = useAuthState();
  const requestCertificate = useRequestCertificate();
  const { isActiveFlag } = useFeatureFlagConfig();
  const { tracker } = useTrackingState();

  useInAppHeader({ screen: WebViewScreen.MyContentDetail, hideHeader: true });
  useInAppStatusBar(WebViewScreen.MyContentDetail);

  const stage = useContestStage(contestId);
  const { data: contestContentsRes } = useGetMyContestContentsQuery(contestId);

  useEffect(() => {
    tracker.track(TrackingEventType.ViewStudioContentList);
  }, [tracker]);

  const tabType = useMemo(
    () => extractEnumValue(StudioContentsTabType, String(searchParams?.get('tabType')), StudioContentsTabType.All),
    [searchParams],
  );

  const [isPickAndChoo4MyStudioContentsTabClicked, setIsPickAndChoo4MyStudioContentsTabClicked] = useState(
    () => !!LocalStorage.getItem(LocalStorageKey.PickAndChoo4MyStudioContentsTabClicked),
  );

  useEffect(() => {
    if (tabType === StudioContentsTabType.Contest && !isPickAndChoo4MyStudioContentsTabClicked) {
      LocalStorage.setItem(LocalStorageKey.PickAndChoo4MyStudioContentsTabClicked, new Date().toISOString());
      setIsPickAndChoo4MyStudioContentsTabClicked(true);
    }
  }, [tabType, isPickAndChoo4MyStudioContentsTabClicked]);

  const { data: sectionData } = useGetSectionsQuery({
    includeAdult: !!user?.isAdult,
    position: SectionPosition.DirittoStudioMyContentsTop,
  });

  const { data: wsContentsRes, isPending: isWsContentsPending } = useGetWsContentsQuery();
  const contentIds = useMemo(() => wsContentsRes?.contentIds ?? [], [wsContentsRes?.contentIds]);
  const { data: relayContentRes, isPending: isRelayContentPending } = usePostRelayWorkspaceContentsQuery({
    contentIds,
    includeAdult: true,
  });
  const isLoading = isWsContentsPending || (contentIds.length > 0 && isRelayContentPending);

  const pickAndChoo4Contents = useMemo(() => {
    const contestContents = contestContentsRes?.contentContests;
    const contestContentIds = contestContents?.filter(cc => !cc.withdrawalAt).map(cc => cc.id) ?? [];

    return contentIds
      .map(contentId => relayContentRes?.contents[contentId])
      .filter(content => content?.relayType === RelayType.Public)
      .filter(content => contestContentIds.includes((content as ContentPublicRelayDto)?.contentContestId ?? 0));
  }, [contentIds, relayContentRes, contestContentsRes]);

  const contentsByTabType = useMemo(() => {
    const pickAndChoo4ContentIds = new Set(pickAndChoo4Contents.map(c => c?.id));

    const result = {
      serializedContents: [] as ContentPublicRelayDto[],
      completedOrPublishedContents: [] as ContentPublicRelayDto[],
      allContents: [] as ContentPublicRelayDto[],
      contestContents: [] as ContentPublicRelayDto[],
    };

    contentIds.forEach(contentId => {
      const content = relayContentRes?.contents[contentId];

      if (!content || content.relayType !== RelayType.Public) {
        return;
      }

      if (pickAndChoo4ContentIds.has(content.id)) {
        result.contestContents.push(content);
        return;
      }

      if (content.status === ContentStatus.Private) {
        result.allContents.push(content);
        return;
      }

      switch (content.serializeStatus) {
        case ContentSerializeStatus.Serialized:
          result.serializedContents.push(content);
          result.allContents.push(content);
          break;
        case ContentSerializeStatus.Completed:
        case ContentSerializeStatus.Published:
          result.completedOrPublishedContents.push(content);
          result.allContents.push(content);
          break;
        default:
          SentryService.captureMessage(`Invalid serializeStatus: ${content.serializeStatus}`);
      }
    });

    return result;
  }, [contentIds, relayContentRes, pickAndChoo4Contents]);

  const contents = useMemo(() => {
    switch (tabType) {
      case StudioContentsTabType.Serialized:
        return contentsByTabType.serializedContents;
      case StudioContentsTabType.CompletedOrPublished:
        return contentsByTabType.completedOrPublishedContents;
      case StudioContentsTabType.All:
        return contentsByTabType.allContents;
      case StudioContentsTabType.Contest:
        return contentsByTabType.contestContents;
      default:
        SentryService.captureMessage(`Invalid tabType: ${tabType}`);
        return [];
    }
  }, [contentsByTabType, tabType]);

  const handleTrack = useCallback(
    (action: ClickAction, clickType: ClickTargetType, position: TrackerPosition) => {
      tracker.track(TrackingEventType.Click, {
        action,
        clickType,
        trackerExtraInfo: { trackerPosition: position },
      });
    },
    [tracker],
  );

  const getTrackerPosition = useCallback(() => {
    switch (tabType) {
      case StudioContentsTabType.Serialized:
        return TrackerPosition.DirittoStudioContentSerializedList;
      case StudioContentsTabType.CompletedOrPublished:
        return TrackerPosition.DirittoStudioContentCompletedOrPublishedList;
      case StudioContentsTabType.Contest:
        return TrackerPosition.DirittoStudioContentPickAndChooList;
      default:
        return TrackerPosition.DirittoStudioContentAllList;
    }
  }, [tabType]);

  const handleAddContentClick = useCallback(() => {
    if (!user?.ageVerified) {
      showAlertModal(
        { title: '본인인증을 해주세요.', description: '해당 기능을 사용하려면 본인인증이 필요해요.' },
        {
          onConfirm: requestCertificate,
          confirmText: '본인인증하러 가기',
          cancelText: '닫기',
        },
      );
      return;
    }

    handleTrack(ClickAction.ToDirittoStudioContentAdd, ClickTargetType.Button, getTrackerPosition());
    router.push(
      tabType === StudioContentsTabType.Contest
        ? `/studio/contests/${contestId}/contents/add?type=${ContentType.Contest}`
        : `/studio/contests/${contestId}/contents/add`,
    );
  }, [handleTrack, requestCertificate, router, user?.ageVerified, tabType, contestId]);

  const listHeaderRightArea = useMemo(
    () => [
      <LinkButton
        key="add"
        href={
          tabType === StudioContentsTabType.Contest
            ? `/studio/contests/${contestId}/contents/add?type=${ContentType.Contest}`
            : `/studio/contests/${contestId}/contents/add`
        }
        label="새 작품"
        onClick={handleAddContentClick}
        size={ButtonSize.XSmall}
        buttonTypography={Typography.XXXSmall}
      />,
    ],
    [handleAddContentClick, tabType, contestId],
  );

  const renderContestDescritipon = useMemo(() => {
    if (stage === ContestStage.Writing) {
      return <p className="font-bold">2026 제4회 픽앤츄가 시작되었어요. 작가님들의 많은 관심과 참여 부탁드려요!</p>;
    }
    if (stage === ContestStage.Applied) {
      return (
        <p className="font-bold">독자 투표는 2026년 1월 28일에 시작되며, 현재는 픽앤츄 접수 종료 후 검수 중이에요.</p>
      );
    }
    if (stage === ContestStage.Approved) {
      return (
        <p className="font-bold">현재 픽앤츄는 독자 투표가 진행 중이에요. (투표 기간 : 2026.01.28 - 2026.02.03)</p>
      );
    }
    if (stage === ContestStage.Accepted) {
      return (
        <p className="font-bold">
          픽앤츄 선정 결과는 2026년 2월 25일 15:00에 발표되며, 현재는 투표 종료 후 심사 중이에요.
        </p>
      );
    }

    return null;
  }, [stage]);

  return (
    <DirittoStudioLayout leftMenu={<DirittoStudioLeftMenu />}>
      {renderContestDescritipon && (
        <DescriptionBlock
          message={renderContestDescritipon}
          className="font-bold"
          isBorderRadius={false}
          bgColor={Color.Primary100}
          color={Color.Primary500}
          icon={<InfoFilledIcon color={Color.Primary500} className="w-4 h-4" />}
        />
      )}
      {sectionData && sectionData.sections.length > 0 && (
        <div className="py-4 flex flex-col">
          {sectionData.sections.map(section => (
            <Section
              key={section.id}
              section={section}
              contents={sectionData.contents}
              episodes={sectionData.episodes}
              profileLites={sectionData.profileLites || {}}
              replies={sectionData.replies}
              fancyShares={sectionData.fancyShares}
              rollings={sectionData.rollings}
              trackerExtraInfo={trackerExtraInfo}
            />
          ))}
        </div>
      )}

      {user?.type !== UserType.Partner && (
        <ListHeader
          className="z-[var(--z-index-navbar)]"
          leftArea={<SectionText className="text-lg font-bold">내 작품</SectionText>}
          rightArea={listHeaderRightArea}
        />
      )}
      <Tab
        leftTabs={
          <TabList gap={32}>
            <StudioContentsTabItem
              selectedTabType={tabType}
              tabType={StudioContentsTabType.All}
              text={`전체 ${contentsByTabType.allContents.length}`}
            />
            <StudioContentsTabItem
              selectedTabType={tabType}
              tabType={StudioContentsTabType.Serialized}
              text={`연재 중 ${contentsByTabType.serializedContents.length}`}
            />
            <StudioContentsTabItem
              selectedTabType={tabType}
              tabType={StudioContentsTabType.CompletedOrPublished}
              text={`완결 / 출간 ${contentsByTabType.completedOrPublishedContents.length}`}
            />
            {isActiveFlag(FeatureFlagName.PickAndChoo4) && (
              <StudioContentsContestTabItem
                isRedDot={!isPickAndChoo4MyStudioContentsTabClicked}
                selectedTabType={tabType}
                tabType={StudioContentsTabType.Contest}
                text="픽앤츄"
                count={pickAndChoo4Contents.length}
              />
            )}
          </TabList>
        }
      />
      {isLoading && <Loading />}
      {!isLoading &&
        (tabType === StudioContentsTabType.Contest ? (
          <MyContestContentList
            contestId={contestId}
            onAddContent={handleAddContentClick}
            contents={pickAndChoo4Contents as ContentPublicRelayDto[]}
            user={user}
          />
        ) : (
          <MyContentList
            tabType={tabType}
            onAddContent={handleAddContentClick}
            contents={contents as ContentPublicRelayDto[]}
            user={user}
          />
        ))}
    </DirittoStudioLayout>
  );
}
